import React, { useState } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useQuery from "../../hooks/useQuery";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../shared/Button";
import AuthCodeInput from "../../shared/AuthCodeInput";
import {
  useResendLoginMfaMutation,
  useVerifyLoginMfaMutation,
} from "../../redux/services/account/accountApi";

const MfaChallenge = () => {
  const queries = useQuery();
  const navigate = useNavigate();
  const { setUser, setParentCompany } = useAuth();
  const [code, setCode] = useState("");
  const challengeId = queries.get("challengeId");
  const email = queries.get("email");
  const maskedDestination = queries.get("maskedDestination");
  const [verifyLoginMfa, verifyState] = useVerifyLoginMfaMutation();
  const [resendLoginMfa, resendState] = useResendLoginMfaMutation();

  async function handleVerify() {
    if (!challengeId) {
      toast.error("Your sign-in verification has expired. Please sign in again.", {
        toastId: "mfa-missing-challenge",
      });
      navigate("/login", { replace: true });
      return;
    }

    if (!code || code.length < 6) {
      toast.error("Please enter the 6-digit verification code from your email.", {
        toastId: "mfa-code-required",
      });
      return;
    }

    try {
      const response = await verifyLoginMfa({
        challengeId,
        code,
      }).unwrap();

      if (response?.status === "success" && response?.user) {
        setUser(response.user);
        setParentCompany(
          response.user.parentCompany || response.user.organization || null
        );
        toast.success(response.message || "Verification complete.", {
          toastId: "mfa-verified",
        });
        navigate("/", { replace: true });
        return;
      }

      toast.error("We couldn't finish signing you in.", {
        toastId: "mfa-invalid-response",
      });
    } catch (error) {
      toast.error(
        error?.data?.message ||
          "We couldn't verify that code. Request a new one and try again.",
        {
          toastId: "mfa-verify-error",
        }
      );
    }
  }

  async function handleResend() {
    if (!challengeId) {
      navigate("/login", { replace: true });
      return;
    }

    try {
      const response = await resendLoginMfa({ challengeId }).unwrap();
      toast.success(response?.message || "A new verification code was sent.", {
        toastId: "mfa-resend",
      });

      navigate(
        `/login/mfa?challengeId=${encodeURIComponent(
          response.challengeId || challengeId
        )}&email=${encodeURIComponent(email || "")}&maskedDestination=${encodeURIComponent(
          response.maskedDestination || maskedDestination || ""
        )}`,
        { replace: true }
      );
    } catch (error) {
      toast.error(
        error?.data?.message ||
          "We couldn't send another code yet. Please wait and try again.",
        {
          toastId: "mfa-resend-error",
        }
      );
    }
  }

  return (
    <div className="flex flex-col items-center justify-center md:justify-start my-auto pt-8 md:pt-0 px-1 md:px-5 lg:px-24">
      <p className="text-center text-4xl font-bold pb-1">Verify Sign In</p>
      <p className="text-center text-md text-gray-600 pb-5">
        Enter the one-time code we sent to {maskedDestination || "your email"}.
      </p>
      <div className="w-3/4">
        <div className="my-3">
          <AuthCodeInput
            value={code}
            onChange={({ value }) => setCode(value)}
            placeholder="Enter verification code"
          />
        </div>

        <div className="my-3">
          <Button
            type="primary"
            disabled={verifyState.isLoading}
            onClick={handleVerify}
            className="w-full"
            loading={verifyState.isLoading}
          >
            Verify Code
          </Button>
        </div>

        <div className="my-3">
          <Button
            type="secondary"
            disabled={resendState.isLoading}
            onClick={handleResend}
            className="w-full"
            loading={resendState.isLoading}
          >
            Resend Code
          </Button>
        </div>

        <div className="pt-5">
          <p
            onClick={() => navigate("/login")}
            className="flex items-center hover:underline cursor-pointer"
          >
            <ChevronLeftIcon className="w-4 h-4 stroke-2" /> Go Back
          </p>
        </div>
      </div>
    </div>
  );
};

export default MfaChallenge;
